import { useMemo, useState } from 'react';
import type {
  ApiReconstructedDocumentDetail,
  ConsolidatedNonExceedanceStatement,
  ConsolidatedPmaDerStatus,
  UtilisationRecordSummary,
} from '@avisoma/shared';
import { findingsByFamily as groupFindingsByFamily } from './extractionTables/findingsByFamily';
import { ADTable } from './extractionTables/ADTable';
import { LLPTable } from './extractionTables/LLPTable';
import { PartMovementTable } from './extractionTables/PartMovementTable';
import { EngineEventsTable } from './extractionTables/EngineEventsTable';
import { LDNDTable } from './extractionTables/LDNDTable';
import { SBTable } from './extractionTables/SBTable';
import { BillOfSaleTable } from './extractionTables/BillOfSaleTable';
import { presentBillOfSaleRows } from './extractionTables/billOfSalePresentation';
import { ARCTable } from './extractionTables/ARCTable';
import { QECTable } from './extractionTables/QECTable';
import { BladeTable } from './extractionTables/BladeTable';
import { WorkscopeTable } from './extractionTables/WorkscopeTable';
import { BorescopeTable } from './extractionTables/BorescopeTable';
import { RepairTable } from './extractionTables/RepairTable';
import { NisTable } from './extractionTables/NisTable';
import { ThrustConversionTable } from './extractionTables/ThrustConversionTable';
import {
  PmaDerDeclarationsTable,
  PmaDerPartsTable,
  presentPmaDerScalarEntries,
} from './extractionTables/PmaDerTables';
import {
  NonExceedanceDeclarationsTable,
  presentNonExceedanceScalarEntries,
} from './extractionTables/NonExceedanceTables';
import { EngineMovementTable } from './extractionTables/EngineMovementTable';
import { SourcePageCell } from './extractionTables/SourcePageCell';
import { RowInspectorDialog } from './extractionTables/RowInspectorDialog';
import { ExtractionTableCard } from './extractionTables/ExtractionTableCard';
import { DocumentPageErrorBox } from '../documentPages/DocumentPageErrorBox';
import { DocumentPageStatusIcon } from '../documentPages/DocumentPageStatusIcon';
import { PrimaryExtractionSkippedNotice } from '../PrimaryExtractionSkippedNotice';
import { entityPrimaryExtractionSkipped } from '../../lib/primaryExtractionSkipped';

export function ConsolidatedExtractionCard({
  entity,
  refreshing = false,
  autoRefreshing = false,
}: {
  entity: ApiReconstructedDocumentDetail;
  refreshing?: boolean;
  autoRefreshing?: boolean;
}) {
  const extraction = entity.consolidatedExtraction;
  const billOfSaleRows = useMemo(
    () =>
      presentBillOfSaleRows(
        extraction?.billOfSaleEvents ?? [],
        extraction?.isDocumentSigned,
      ),
    [extraction?.billOfSaleEvents, extraction?.isDocumentSigned],
  );
  // DR-1630: each row in consolidatedExtraction now carries a source pageId.
  // Build the pageId → 1-based position lookup once so each table can render
  // a friendly "→ Page N" link without re-walking the pages array per row.
  const pageIdToPosition = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of entity.pages) map.set(p.pageId, p.position + 1);
    return map;
  }, [entity.pages]);
  const findingsByFamily = useMemo(
    () => groupFindingsByFamily(entity.supervisorFindings ?? []),
    [entity.supervisorFindings],
  );
  const total =
    (extraction?.adStatusEvents?.length ?? 0) +
    (extraction?.sbStatusEvents?.length ?? 0) +
    (extraction?.llpStatusEvents?.length ?? 0) +
    (extraction?.partMovementEvents?.length ?? 0) +
    (extraction?.engineEvents?.length ?? 0) +
    (extraction?.ldndStatusEvents?.length ?? 0) +
    (extraction?.billOfSaleEvents?.length ?? 0) +
    (extraction?.arcEvents?.length ?? 0) +
    (extraction?.qecListEvents?.length ?? 0) +
    (extraction?.bladeStatusEvents?.length ?? 0) +
    (extraction?.workscopeEvents?.length ?? 0) +
    (extraction?.borescopeInspections?.length ?? 0) +
    (extraction?.repairRecords?.length ?? 0) +
    (extraction?.nisStatementEvents?.length ?? 0) +
    (extraction?.thrustConversionEvents?.length ?? 0) +
    (extraction?.engineMovementEvents?.length ?? 0) +
    (extraction?.pmaDerStatus
      ? (extraction.pmaDerStatus.declarations?.length ?? 0) +
        (extraction.pmaDerStatus.parts?.length ?? 0)
      : 0) +
    (extraction?.nonExceedanceStatement?.declarations.length ?? 0);

  // Only the row's identity: the findings and the row are read from the current entity at render
  // time, so a Revert's refetch updates the open inspector instead of leaving a stale status with
  // Revert enabled. The stored `data` is the fallback (and the only source for family-less rows).
  const [inspected, setInspected] = useState<{
    title: string;
    data: unknown;
    family?: string;
    index?: number;
  } | null>(null);
  const inspect = (title: string, family?: string) => (row: unknown, index?: number) =>
    setInspected({ title, data: row, family, index });
  const inspectedFindings =
    inspected?.family !== undefined && inspected.index !== undefined
      ? (findingsByFamily.get(inspected.family)?.get(inspected.index) ?? [])
      : [];
  // The row is read live too, so a Revert's refetch shows the restored value. Keyed by index: a
  // revert that moves the row shows the row now at that index.
  const rowsFor = (family: string): readonly unknown[] | undefined =>
    family === 'billOfSaleEvents'
      ? billOfSaleRows
      : ((extraction as Record<string, unknown> | null | undefined)?.[family] as unknown[] | undefined);
  const inspectedData =
    inspected?.family !== undefined && inspected.index !== undefined
      ? (rowsFor(inspected.family)?.[inspected.index] ?? inspected.data)
      : inspected?.data;

  return (
    <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow sm:rounded-lg p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-gray-700 dark:text-zinc-200">
          Consolidated extraction
          {extraction && (
            <span className="ml-2 text-xs text-gray-500 dark:text-zinc-500">
              {extraction.utilisationRecord
                ? total > 0
                  ? `${total} events + utilisation record`
                  : 'utilisation record'
                : `${total} events`}
            </span>
          )}
        </h2>
        <span className="text-xs text-gray-500 dark:text-zinc-500">
          {refreshing
            ? 'Refreshing extraction…'
            : autoRefreshing
              ? 'Auto-refreshing while pages process…'
              : "Refreshed when any constituent page's extraction completes"}
        </span>
      </div>

      {entity.extraction && (
        <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
          <DocumentPageStatusIcon
            status={entity.extraction.status === 'pending' ? 'processing' : entity.extraction.status}
          />
          <span>
            Extraction {entity.extraction.status}
            {entity.extraction.category ? ` · ${entity.extraction.category}` : ''}
            {entity.extraction.skipReason ? ` (${entity.extraction.skipReason})` : ''}
          </span>
        </div>
      )}
      {entityPrimaryExtractionSkipped(entity.extraction) && (
        <PrimaryExtractionSkippedNotice hash="user-suggested" category={entity.extraction?.category} />
      )}
      {entity.extraction?.status === 'error' && entity.extraction.errorMessage && (
        <div className="mt-2">
          <DocumentPageErrorBox error={entity.extraction.errorMessage} />
        </div>
      )}

      {!extraction ? (
        <div className="mt-3 text-sm text-gray-500 dark:text-zinc-500">
          No extraction yet. Runs after document reconstruction completes for this entity.
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          {extraction.adStatusEvents && extraction.adStatusEvents.length > 0 && (
            <ExtractionTableCard
              label="AD Status"
              rows={extraction.adStatusEvents}
              csvTitle="AD Status"
              bodyHeight="consolidated"
            >
              <ADTable
                rows={extraction.adStatusEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('AD Status row', 'adStatusEvents')}
                rowFindings={findingsByFamily.get('adStatusEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.sbStatusEvents && extraction.sbStatusEvents.length > 0 && (
            <ExtractionTableCard
              label="SB Status"
              rows={extraction.sbStatusEvents}
              csvTitle="SB Status"
              bodyHeight="consolidated"
            >
              <SBTable
                rows={extraction.sbStatusEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('SB Status row', 'sbStatusEvents')}
                rowFindings={findingsByFamily.get('sbStatusEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.llpStatusEvents && extraction.llpStatusEvents.length > 0 && (
            <ExtractionTableCard
              label="LLP Status"
              rows={extraction.llpStatusEvents}
              csvTitle="LLP Status"
              bodyHeight="consolidated"
            >
              <LLPTable
                rows={extraction.llpStatusEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('LLP Status row', 'llpStatusEvents')}
                rowFindings={findingsByFamily.get('llpStatusEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.partMovementEvents && extraction.partMovementEvents.length > 0 && (
            <ExtractionTableCard
              label="Part Movement"
              rows={extraction.partMovementEvents}
              csvTitle="Part Movement"
              bodyHeight="consolidated"
            >
              <PartMovementTable
                rows={extraction.partMovementEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Part Movement row', 'partMovementEvents')}
                rowFindings={findingsByFamily.get('partMovementEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.engineEvents && extraction.engineEvents.length > 0 && (
            <ExtractionTableCard
              label="Engine Events"
              rows={extraction.engineEvents}
              csvTitle="Engine Events"
              bodyHeight="consolidated"
            >
              <EngineEventsTable
                rows={extraction.engineEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Engine Event row', 'engineEvents')}
                rowFindings={findingsByFamily.get('engineEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.ldndStatusEvents && extraction.ldndStatusEvents.length > 0 && (
            <ExtractionTableCard
              label="LDND"
              rows={extraction.ldndStatusEvents}
              csvTitle="LDND"
              bodyHeight="consolidated"
            >
              <LDNDTable
                rows={extraction.ldndStatusEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('LDND row', 'ldndStatusEvents')}
                rowFindings={findingsByFamily.get('ldndStatusEvents')}
              />
            </ExtractionTableCard>
          )}
          {billOfSaleRows.length > 0 && (
            <ExtractionTableCard
              label="Bill of Sale"
              rows={billOfSaleRows}
              csvTitle="Bill of Sale"
              bodyHeight="consolidated"
            >
              <BillOfSaleTable
                rows={billOfSaleRows}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Bill of Sale row', 'billOfSaleEvents')}
                rowFindings={findingsByFamily.get('billOfSaleEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.arcEvents && extraction.arcEvents.length > 0 && (
            <ExtractionTableCard
              label="ARC"
              rows={extraction.arcEvents}
              csvTitle="ARC"
              bodyHeight="consolidated"
            >
              <ARCTable
                arcEvents={extraction.arcEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('ARC row', 'arcEvents')}
                rowFindings={findingsByFamily.get('arcEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.qecListEvents && extraction.qecListEvents.length > 0 && (
            <ExtractionTableCard
              label="QEC / LRU List"
              rows={extraction.qecListEvents}
              csvTitle="QEC / LRU List"
              bodyHeight="consolidated"
            >
              <QECTable
                rows={extraction.qecListEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('QEC / LRU row', 'qecListEvents')}
                rowFindings={findingsByFamily.get('qecListEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.bladeStatusEvents && extraction.bladeStatusEvents.length > 0 && (
            <ExtractionTableCard
              label="Blades & nozzles"
              rows={extraction.bladeStatusEvents}
              csvTitle="Blades & nozzles"
              bodyHeight="consolidated"
            >
              <BladeTable
                rows={extraction.bladeStatusEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Blade / nozzle row', 'bladeStatusEvents')}
                rowFindings={findingsByFamily.get('bladeStatusEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.workscopeEvents && extraction.workscopeEvents.length > 0 && (
            <ExtractionTableCard
              label="Engine Workscope"
              rows={extraction.workscopeEvents}
              csvTitle="Engine Workscope"
              bodyHeight="consolidated"
            >
              <WorkscopeTable
                rows={extraction.workscopeEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Engine Workscope row', 'workscopeEvents')}
                rowFindings={findingsByFamily.get('workscopeEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.borescopeInspections && extraction.borescopeInspections.length > 0 && (
            <ExtractionTableCard
              label="Borescope Report"
              rows={extraction.borescopeInspections}
              csvTitle="Borescope Report"
              bodyHeight="consolidated"
            >
              <BorescopeTable
                rows={extraction.borescopeInspections}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Borescope Report row', 'borescopeInspections')}
                rowFindings={findingsByFamily.get('borescopeInspections')}
              />
            </ExtractionTableCard>
          )}
          {extraction.repairRecords && extraction.repairRecords.length > 0 && (
            <ExtractionTableCard
              label="Repair Report"
              rows={extraction.repairRecords}
              csvTitle="Repair Report"
              bodyHeight="consolidated"
            >
              <RepairTable
                rows={extraction.repairRecords}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Repair Report row', 'repairRecords')}
                rowFindings={findingsByFamily.get('repairRecords')}
              />
            </ExtractionTableCard>
          )}
          {extraction.nisStatementEvents && extraction.nisStatementEvents.length > 0 && (
            <ExtractionTableCard
              label="NIS Statement"
              rows={extraction.nisStatementEvents}
              csvTitle="NIS Statement"
              bodyHeight="consolidated"
            >
              <NisTable
                rows={extraction.nisStatementEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('NIS Statement row', 'nisStatementEvents')}
                rowFindings={findingsByFamily.get('nisStatementEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.thrustConversionEvents && extraction.thrustConversionEvents.length > 0 && (
            <ExtractionTableCard
              label="Thrust Rating Change Records"
              rows={extraction.thrustConversionEvents}
              csvTitle="Thrust Rating Change Records"
              bodyHeight="consolidated"
            >
              <ThrustConversionTable
                rows={extraction.thrustConversionEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Thrust Conversion row', 'thrustConversionEvents')}
                rowFindings={findingsByFamily.get('thrustConversionEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.pmaDerStatus && (
            <PmaDerConsolidatedSection
              status={extraction.pmaDerStatus}
              pageIdToPosition={pageIdToPosition}
              inspect={inspect}
            />
          )}
          {extraction.nonExceedanceStatement && (
            <NonExceedanceConsolidatedSection
              statement={extraction.nonExceedanceStatement}
              pageIdToPosition={pageIdToPosition}
              inspect={inspect}
            />
          )}
          {extraction.engineMovementEvents && extraction.engineMovementEvents.length > 0 && (
            <ExtractionTableCard
              label="Engine Install/Removal Job Card"
              rows={extraction.engineMovementEvents}
              csvTitle="Engine Install/Removal Job Card"
              bodyHeight="consolidated"
            >
              <EngineMovementTable
                rows={extraction.engineMovementEvents}
                pageIdToPosition={pageIdToPosition}
                onInspect={inspect('Engine Movement row', 'engineMovementEvents')}
                rowFindings={findingsByFamily.get('engineMovementEvents')}
              />
            </ExtractionTableCard>
          )}
          {extraction.utilisationRecord && (
            <Section label="Utilisation Record">
              <UtilisationSummary
                record={extraction.utilisationRecord}
                pageIdToPosition={pageIdToPosition}
              />
            </Section>
          )}
        </div>
      )}

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title={inspected?.title ?? ''}
        data={inspectedData}
        findings={inspected ? inspectedFindings : undefined}
      />
    </div>
  );
}

function UtilisationSummary({
  record,
  pageIdToPosition,
}: {
  record: UtilisationRecordSummary;
  pageIdToPosition: Map<string, number>;
}) {
  const fields = (
    [
      ['Report Date', record.reportDate],
      ['Latest Flight Date', record.latestFlightDate],
      ['Earliest Flight Date', record.earliestFlightDate],
      ['Total Air Hours (TAH)', record.latestTAH],
      ['Total Air Cycles (TAC)', record.latestTAC],
      ['Aircraft Registration', record.aircraftRegistration],
    ] as [string, string | number | undefined][]
  ).filter((entry): entry is [string, string | number] => entry[1] != null);

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 text-sm sm:grid-cols-3">
      {fields.map(([label, value]) => (
        <div key={label}>
          <dt className="text-xs font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
          <dd className="mt-0.5 text-gray-900 dark:text-zinc-100">{String(value)}</dd>
        </div>
      ))}
      <div>
        <dt className="text-xs font-medium text-gray-500 dark:text-zinc-400">Source Page</dt>
        <dd className="mt-0.5">
          <SourcePageCell pageId={record.sourcePageId} pageIdToPosition={pageIdToPosition} />
        </dd>
      </div>
    </dl>
  );
}

function PmaDerConsolidatedSection({
  status,
  pageIdToPosition,
  inspect,
}: {
  status: ConsolidatedPmaDerStatus;
  pageIdToPosition: Map<string, number>;
  inspect: (title: string) => (row: unknown) => void;
}) {
  const scalars = presentPmaDerScalarEntries(status);
  const declarations = status.declarations ?? [];
  const parts = status.parts ?? [];
  if (scalars.length === 0 && declarations.length === 0 && parts.length === 0) {
    return null;
  }
  return (
    <>
      {scalars.length > 0 && (
        <Section label="PMA/DER Status">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 text-sm sm:grid-cols-3">
            {scalars.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                <dd
                  className={
                    label === 'ESNs'
                      ? 'mt-0.5 font-mono text-gray-900 dark:text-zinc-100'
                      : 'mt-0.5 text-gray-900 dark:text-zinc-100'
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
      {declarations.length > 0 && (
        <ExtractionTableCard
          label="PMA/DER Declarations"
          rows={declarations}
          csvTitle="PMA-DER Declarations"
          bodyHeight="consolidated"
        >
          <PmaDerDeclarationsTable
            rows={declarations}
            pageIdToPosition={pageIdToPosition}
            onInspect={inspect('PMA/DER declaration')}
          />
        </ExtractionTableCard>
      )}
      {parts.length > 0 && (
        <ExtractionTableCard
          label="PMA/DER Parts"
          rows={parts}
          csvTitle="PMA-DER Parts"
          bodyHeight="consolidated"
        >
          <PmaDerPartsTable
            rows={parts}
            pageIdToPosition={pageIdToPosition}
            onInspect={inspect('PMA/DER part')}
          />
        </ExtractionTableCard>
      )}
    </>
  );
}

function NonExceedanceConsolidatedSection({
  statement,
  pageIdToPosition,
  inspect,
}: {
  statement: ConsolidatedNonExceedanceStatement;
  pageIdToPosition: Map<string, number>;
  inspect: (title: string) => (row: unknown) => void;
}) {
  const scalars = presentNonExceedanceScalarEntries(statement);
  if (statement.declarations.length === 0) return null;
  return (
    <>
      {scalars.length > 0 && (
        <Section label="Non-Exceedance Statement">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 text-sm sm:grid-cols-3">
            {scalars.map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
                <dd
                  className={
                    label === 'ESNs'
                      ? 'mt-0.5 font-mono text-gray-900 dark:text-zinc-100'
                      : 'mt-0.5 text-gray-900 dark:text-zinc-100'
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      )}
      <ExtractionTableCard
        label="Non-Exceedance Declarations"
        rows={statement.declarations}
        csvTitle="Non-Exceedance Declarations"
        bodyHeight="consolidated"
      >
        <NonExceedanceDeclarationsTable
          rows={statement.declarations}
          pageIdToPosition={pageIdToPosition}
          onInspect={inspect('Non-exceedance declaration')}
        />
      </ExtractionTableCard>
    </>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 dark:border-zinc-700 rounded-md overflow-hidden">
      <div className="bg-gray-50 dark:bg-zinc-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </div>
      <div className="max-h-[400px] overflow-auto">{children}</div>
    </div>
  );
}
